import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChefHat, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WelcomePage = ({ onEnter }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email");
    } else {
      setEmailError("");
    }
  };

  const handleEnter = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!validateEmail(email)) {
      toast.error("Please enter a valid email");
      return;
    }

    setIsLoading(true);
    try {
      // Call backend to login/register
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        name: name.trim()
      });

      const userData = response.data;
      
      // Clear previous user's state if different user or fresh session
      const prevEmail = localStorage.getItem("userEmail");
      if (!prevEmail || prevEmail !== userData.email) {
        localStorage.removeItem("mealPlans");
        localStorage.removeItem("todaysMeals");
        localStorage.removeItem("todaysMealsDate");
        localStorage.removeItem("hasSeenWelcome");
        localStorage.removeItem("learningBannerDismissed");
        localStorage.removeItem("userSignupDate");
        localStorage.removeItem("memberId");
      }

      // Save to localStorage
      localStorage.setItem("userName", userData.name);
      localStorage.setItem("userEmail", userData.email);
      localStorage.setItem("userId", userData.id);

      // Notify App.js to update login state
      window.dispatchEvent(new Event("loginStateChanged"));

      setIsAnimating(true);
      setTimeout(() => {
        onEnter(userData.name, userData.email, userData);
        if (userData.is_new) {
          toast.success(`Welcome, ${userData.name}!`);
        } else {
          toast.success(`Welcome back, ${userData.name}!`);
        }
      }, 300);
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleEnter();
    }
  };

  const benefits = [
    {
      title: "No More Decision Fatigue",
      description: "Stop overthinking what to eat. We'll suggest, you just pick."
    },
    {
      title: "Peace at Mealtimes",
      description: "End the daily 'what to cook?' stress with simple choices."
    },
    {
      title: "Food That Feels Right",
      description: "Comfort meals suited to your day, time, and mood."
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-6 py-12 transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`} data-testid="welcome-page">
      {/* Logo & Heading */}
      <div className="text-center mb-10 animate-fade-in-up">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <ChefHat size={40} className="text-primary" strokeWidth={1.5} />
        </div>
        <h1 className="heading-display text-3xl md:text-5xl mb-3">
          Aaj kya khayenge
        </h1>
        <p className="text-body text-lg md:text-xl max-w-md mx-auto">
          Let food decisions be simple again.
        </p>
      </div>

      {/* Name & Email Input Section */}
      <div className="w-full max-w-sm mb-12 animate-fade-in-up animation-delay-200 space-y-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">
            Your Name
          </label>
          <Input
            type="text"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={handleKeyPress}
            className="h-12 rounded-2xl text-base border-2 border-border focus:border-primary/50 bg-white"
            data-testid="name-input"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">
            Email ID
          </label>
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={handleEmailChange}
            onKeyPress={handleKeyPress}
            className={`h-12 rounded-2xl text-base border-2 bg-white ${emailError ? 'border-destructive' : 'border-border focus:border-primary/50'}`}
            data-testid="email-input"
          />
          {emailError && (
            <p className="text-destructive text-xs mt-1">{emailError}</p>
          )}
        </div>
        <Button
          onClick={handleEnter}
          disabled={isLoading}
          className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 text-base"
          data-testid="enter-btn"
        >
          {isLoading ? (
            <RefreshCw size={20} className="animate-spin" />
          ) : (
            <>
              Get Started
              <ArrowRight size={20} className="ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Benefits Section - Without Icons */}
      <div className="w-full max-w-3xl animate-fade-in-up animation-delay-300">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="text-center p-6 rounded-3xl bg-white/50 border border-border/30 hover:bg-white hover:shadow-sm transition-all duration-300"
              style={{ animationDelay: `${300 + index * 100}ms` }}
              data-testid={`benefit-${index + 1}`}
            >
              <h3 className="font-semibold text-foreground mb-2">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer tagline */}
      <p className="text-xs text-muted-foreground/60 mt-12 animate-fade-in animation-delay-400">
        Because food should nourish, not stress.
      </p>
    </div>
  );
};

export default WelcomePage;
